import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Observable, catchError, finalize, of, take } from 'rxjs';

import { EntityTypeConstants } from '../common/EntityTypeConstants';
import { FORMATTING_LOCALES, languageFor } from '../common/formatting-locale';
import { Setting } from '../common/Setting';
import { ExtendedProfile } from '../common/ExtendedProfile';
import { SettingService } from './setting.service';
import { UIConfigurationService } from './uiconfiguration.service';
import { WindowRef } from './windowref.service';

/**
 * The setting key, at both scopes (backend #682).
 *
 * Profile-scoped it is the **reading** language: the durable, cross-device preference behind the
 * page a user is served. Customer-scoped it is the language of what the customer's account
 * *sends* — its seller order notices, and the invoice PDFs it issues — which is a different
 * decision by a different person (an admin), and is why the two live in different tabs.
 */
export const LANGUAGE_SETTING = 'Language';

/** Set once before a redirect, so a bundle that cannot honour it is not asked twice. */
const REDIRECT_MARKER = 'languageRedirect';

/**
 * Owns the rule #178 settled: **the bundle is what the user sees, the setting is what the user
 * wants.**
 *
 * There were four candidate sources of truth — the rendered bundle, `localStorage`, the device,
 * and the profile-scoped setting the backend added — and the app used to mix them. The split is
 * now:
 *
 * - **`LOCALE_ID`, the bundle, decides every per-request value.** `Accept-Language`, `ui_locales`
 *   and formatting all come from it (slice A), so the page and the data on it can never disagree.
 * - **The profile setting decides which bundle the user is served**, on any device. It is read
 *   once when a profile arrives and written on every switch.
 * - **`localStorage` is a cache.** Nothing reads it to make a decision any more — `assets/lang.js`
 *   writes it before bootstrap and the language menu used to read it — so this keeps it in step
 *   with the bundle rather than letting it rot into a value that contradicts the page.
 * - **The device** only ever seeds a first anonymous visit, through `assets/lang.js` and nginx.
 *
 * Reconciling the first two means exactly one full page load, and only when they disagree.
 */
@Injectable({ providedIn: 'root' })
export class LanguagePreferenceService {
  private settingService = inject(SettingService);
  private uiConfiguration = inject(UIConfigurationService);
  private windowRef = inject(WindowRef);

  /** The language actually on screen. */
  public readonly bundleLanguage: string;

  /** The languages this build ships as bundles — `de` and `en`, the nginx/Docker folder names. */
  public readonly supported: ReadonlyArray<string> = Object.freeze(Object.keys(FORMATTING_LOCALES));

  private synced = false;

  constructor() {
    const localeId = inject(LOCALE_ID);

    this.bundleLanguage = languageFor(localeId);
  }

  /**
   * True for a value this build can actually serve. Deliberately stricter than `languageFor`,
   * which folds anything unknown onto the house default: folding here would turn a stored `fr`
   * into a redirect to `/de/`, whose bundle then disagrees with the setting again — a loop.
   */
  public isSupported(value: string | null | undefined): boolean {
    return !!value && this.supported.includes(value);
  }

  /**
   * Points the cached value at the bundle on screen. Called once at start-up so an anonymous
   * visitor routed to `/en/` by nginx does not leave `assets/lang.js`'s guess behind as a value
   * that contradicts the page.
   */
  public rememberBundle(): void {
    this.uiConfiguration.Language = this.bundleLanguage;
  }

  /**
   * Reconciles the setting with the bundle, once per app load, as soon as a profile exists.
   *
   * Three outcomes:
   * - **no row** — the visitor's current bundle becomes their initial preference. Without this an
   *   English visitor who registers would be sent to `/de/` on their next sign-in, because the
   *   backend answers a missing setting with the house default.
   * - **the same language** — nothing happens, which is the ordinary case.
   * - **a different language** — one redirect, and the rest of the app comes up in the language
   *   the user chose, wherever they chose it.
   *
   * Nothing here is allowed to break the page it runs on: a failing read leaves the user on the
   * bundle they already have.
   */
  public syncWithProfile(profile: ExtendedProfile | null): void {
    if (this.synced || !profile?.id) {
      return;
    }
    this.synced = true;

    this.settingService
      .getByReference(profile.id, EntityTypeConstants.EntityTypeProfile)
      .pipe(take(1))
      .subscribe({
        next: (settings) => {
          const stored = settings?.find((s) => s.name === LANGUAGE_SETTING);

          if (!this.isSupported(stored?.value)) {
            this.adopt(profile.id, stored ?? null);
            return;
          }

          this.uiConfiguration.Language = stored.value;

          if (stored.value === this.bundleLanguage) {
            this.clearRedirectMarker();
            return;
          }
          this.redirectTo(stored.value);
        },
        // A read that failed says nothing about what is stored — treating it as "no row" and
        // adopting would let one transient 500 overwrite a preference the user set on another
        // device with whatever bundle this one happens to show. Stay put instead.
        error: () => undefined,
      });
  }

  /**
   * Writes the preference and switches the page to it — the language menu and the settings page
   * both end here.
   *
   * The redirect happens whether or not the write succeeded. What the user asked for is to read
   * the app in another language; refusing them that because a preference could not be persisted
   * would trade a small failure for a visible one. A profile-less visitor skips the write
   * entirely and simply switches bundle.
   */
  public apply(language: string, profileId: string | null): Observable<unknown> {
    if (!this.isSupported(language)) {
      return of(null);
    }

    this.uiConfiguration.Language = language;

    const write$ = profileId
      ? this.write(language, profileId, EntityTypeConstants.EntityTypeProfile, null)
      : of(null);

    return write$.pipe(
      catchError(() => of(null)),
      finalize(() => this.redirectTo(language)),
    );
  }

  /**
   * Switches the page to `language`. Public so the settings page can save first, then switch.
   *
   * Returns whether it actually navigated — a caller that has follow-up work (re-reading the
   * settings it just saved) must skip it when the page is going away, and must still do it when
   * it is not.
   */
  public redirectTo(language: string): boolean {
    if (language === this.bundleLanguage) {
      this.clearRedirectMarker();
      return false;
    }
    // Under `ng serve` there is one bundle and `/en/` is answered by the history fallback with
    // that same bundle, so the reconciliation above would ask for the redirect again on every
    // load. The marker makes that a single wasted reload instead of an endless one; in a
    // localized build it is cleared on arrival, because bundle and setting then agree.
    if (this.readRedirectMarker() === language) {
      console.warn(
        `[language] already redirected to /${language}/ and the bundle is still ` +
          `${this.bundleLanguage} — serving one bundle only (ng serve?), staying put.`,
      );
      return false;
    }
    this.writeRedirectMarker(language);
    // Trailing slash matters: nginx routes with `location ^~ /de/`, which a slash-less `/de`
    // does not match — it would fall through to the catch-all and 302 back into the German app.
    this.windowRef.nativeWindow.location.href = `/${language}/`;
    return true;
  }

  /** First sign-in on a profile that has no row: the bundle in front of the user becomes it. */
  private adopt(profileId: string, existing: Setting | null): void {
    this.uiConfiguration.Language = this.bundleLanguage;
    this.clearRedirectMarker();
    this.write(this.bundleLanguage, profileId, EntityTypeConstants.EntityTypeProfile, existing)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private write(
    language: string,
    referenceId: string,
    referenceType: string,
    existing: Setting | null,
  ): Observable<unknown> {
    const setting = {
      ...(existing ?? { name: LANGUAGE_SETTING, referenceType, referenceId }),
      value: language,
    } as Setting;
    return this.settingService.createOrUpdateMultiple([setting]);
  }

  // sessionStorage rather than a field: the guard has to survive the very page load it guards
  // against, and it must not outlive the tab.
  private readRedirectMarker(): string | null {
    try {
      return sessionStorage.getItem(REDIRECT_MARKER);
    } catch {
      return null;
    }
  }

  private writeRedirectMarker(language: string): void {
    try {
      sessionStorage.setItem(REDIRECT_MARKER, language);
    } catch {
      /* a blocked sessionStorage costs the guard, not the redirect */
    }
  }

  private clearRedirectMarker(): void {
    try {
      sessionStorage.removeItem(REDIRECT_MARKER);
    } catch {
      /* see writeRedirectMarker */
    }
  }
}
